import { Injectable } from '@angular/core';
import { AuthToken } from '../models/auth-token';
import { SessionStorageUtils } from '../../shared/utils/session-storage.utils';
import { IDS_TOKEN, TOKEN_INFO } from '../constants/constants';
import { StringUtils } from '../../shared/utils/string.utils';
import { LocalStorageUtils } from '../../shared/utils/local-storage.utils';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private authToken: AuthToken;

  private persistUserRoles = ['districtadministrator', 'administrator', 'teacher'];
  private shouldPersistInLocalStorage = null;

  constructor() {
    const persistedTokenInfo = LocalStorageUtils.get<string>(TOKEN_INFO);
    if (persistedTokenInfo) {
      try {
        const authToken: AuthToken = JSON.parse(window.atob(persistedTokenInfo));
        if (authToken?.idToken && authToken?.mpToken && authToken?.lsToken) {
          this.authToken = authToken;
          return;
        }
      } catch {
        console.warn('Failed to parse token');
      }
    }

    this.authToken = {
      idToken: SessionStorageUtils.get(IDS_TOKEN) ?? null,
      mpToken: null,
      fluencyToken: null,
      screenerToken: null,
      phonicsToken: null,
      grammarToken: null,
      vocabularyToken: null,
      comprehensionToken: null,
      paToken: null,
      lsToken: null,
      lsShardKey: null,
    };
  }

  public setAuthToken(authToken: Partial<AuthToken>) {
    this.authToken = { ...this.authToken, ...authToken };

    // Store ID token in session for when browser reloads
    if (authToken.idToken) {
      SessionStorageUtils.save(IDS_TOKEN, authToken.idToken);

      // Check if this user allowed to persist in local storage (if not already, no need to parse token every time)
      if (this.shouldPersistInLocalStorage === null) {
        const idTokenInfo = StringUtils.parseJwt(authToken.idToken);
        this.shouldPersistInLocalStorage = this.persistUserRoles.includes(idTokenInfo.role);
      }
    }

    if (this.shouldPersistInLocalStorage) {
      LocalStorageUtils.set(TOKEN_INFO, window.btoa(JSON.stringify(this.authToken)), { ttl: 3600 * 48 });
    }
  }

  public getAuthToken(): AuthToken {
    return this.authToken;
  }

  public getAuthTokenByKey(key: keyof AuthToken): string {
    return this.authToken[key];
  }

  public isAuthenticatedForAuthType(key: keyof AuthToken) {
    return !!this.authToken[key];
  }

  public clearAuthToken() {
    this.authToken = {
      idToken: null,
      mpToken: null,
      fluencyToken: null,
      screenerToken: null,
      phonicsToken: null,
      grammarToken: null,
      vocabularyToken: null,
      comprehensionToken: null,
      paToken: null,
      lsToken: null,
      lsShardKey: null,
    };
    this.shouldPersistInLocalStorage = null;
    SessionStorageUtils.clear();
    LocalStorageUtils.remove(TOKEN_INFO);
  }
}
